/* ============================================================================
   Cromo de la aplicación: barra superior translúcida bajo la cual corre el
   contenido, navegación por pestañas y control de apariencia.

   · El material es una capa flotante, no una franja opaca que come alto fijo.
   · Borde de scroll (máscara de desvanecido), no un divisor de 1px permanente.
   · Las pestañas usan el patrón tablist: ←/→ mueven, Home/End a los extremos.
   ========================================================================== */

import { useEffect, useRef, useState } from 'react'
import { SPRING, SpringValue } from '../lib/spring'
import './chrome.css'

export type ThemeChoice = 'auto' | 'light' | 'dark'

const THEME_ORDER: ThemeChoice[] = ['auto', 'light', 'dark']
const THEME_LABEL: Record<ThemeChoice, string> = {
  auto: 'automática',
  light: 'clara',
  dark: 'oscura',
}
const STORAGE_KEY = 'canasta-bamx:theme'

export function useTheme(): [ThemeChoice, (next: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(() => {
    if (typeof localStorage === 'undefined') return 'auto'
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'auto'
  })

  useEffect(() => {
    const root = document.documentElement
    if (choice === 'auto') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', choice)
    try {
      if (choice === 'auto') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, choice)
    } catch {
      /* modo privado: la preferencia simplemente no persiste */
    }
  }, [choice])

  return [choice, setChoice]
}

function ThemeButton({
  choice,
  onChange,
}: {
  choice: ThemeChoice
  onChange: (next: ThemeChoice) => void
}) {
  const next = THEME_ORDER[(THEME_ORDER.indexOf(choice) + 1) % THEME_ORDER.length]!
  return (
    <button
      type="button"
      className="chrome__theme"
      onClick={() => onChange(next)}
      aria-label={`Apariencia ${THEME_LABEL[choice]}. Cambiar a ${THEME_LABEL[next]}.`}
      title={`Apariencia ${THEME_LABEL[choice]}`}
    >
      {choice === 'auto' ? (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="5.25" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M8 2.75a5.25 5.25 0 000 10.5z" fill="currentColor" />
        </svg>
      ) : choice === 'light' ? (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="3.1" fill="currentColor" />
          <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
            <path d="M8 1.4v1.6M8 13v1.6M1.4 8h1.6M13 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M12.6 3.4l-1.1 1.1M4.5 11.5l-1.1 1.1" />
          </g>
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M13.2 9.8A5.6 5.6 0 016.2 2.8a5.6 5.6 0 107 7z"
            fill="currentColor"
          />
        </svg>
      )}
    </button>
  )
}

function BrandMark() {
  return (
    <svg
      className="chrome__mark"
      width="26"
      height="26"
      viewBox="0 0 26 26"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0.5" y="0.5" width="25" height="25" rx="7" className="chrome__mark-bg" />
      {/* Canasta: asa y cuerpo, con tres niveles que insinúan la serie mensual. */}
      <path
        d="M9.2 9.4a3.8 3.8 0 017.6 0"
        fill="none"
        className="chrome__mark-stroke"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M5.8 9.6h14.4l-1.5 8.1a2 2 0 01-2 1.6H9.3a2 2 0 01-2-1.6z"
        fill="none"
        className="chrome__mark-stroke"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M10.5 12.6v4M13 11.4v5.2M15.5 13.8v2.8"
        fill="none"
        className="chrome__mark-accent"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

export interface NavItem<T extends string> {
  id: T
  label: string
}

export interface ChromeProps<T extends string> {
  items: readonly NavItem<T>[]
  current: T
  onNavigate: (id: T) => void
  /** Texto del chip de estado, p. ej. «Datos a mayo de 2026». */
  status?: string
  /** Estado de la última ingesta. El punto verde no puede ser incondicional. */
  statusTone?: 'ok' | 'error'
  onStatusClick?: () => void
}

export function Chrome<T extends string>({
  items,
  current,
  onNavigate,
  status,
  statusTone = 'ok',
  onStatusClick,
}: ChromeProps<T>) {
  const [scrolled, setScrolled] = useState(false)
  const [theme, setTheme] = useTheme()
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const listRef = useRef<HTMLDivElement | null>(null)
  const indicatorRef = useRef<HTMLSpanElement | null>(null)
  const xSpring = useRef<SpringValue | null>(null)
  const wSpring = useRef<SpringValue | null>(null)
  const primed = useRef(false)

  if (xSpring.current === null) xSpring.current = new SpringValue(0, SPRING.move)
  if (wSpring.current === null) wSpring.current = new SpringValue(0, SPRING.move)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const sx = xSpring.current!
    const sw = wSpring.current!
    const write = () => {
      const el = indicatorRef.current
      if (!el) return
      el.style.transform = `translate3d(${sx.value}px, 0, 0)`
      el.style.width = `${sw.value}px`
    }
    const offX = sx.onChange(write)
    const offW = sw.onChange(write)
    write()
    return () => {
      offX()
      offW()
    }
  }, [])

  useEffect(() => {
    const btn = tabRefs.current.get(current)
    if (!btn) return
    const x = btn.offsetLeft
    const w = btn.offsetWidth
    if (!primed.current) {
      xSpring.current!.set(x)
      wSpring.current!.set(w)
      primed.current = true
    } else {
      xSpring.current!.to(x)
      wSpring.current!.to(w)
    }
    /*
       Mantiene la pestaña activa a la vista en pantallas angostas moviendo SÓLO
       el scroll horizontal del contenedor. `scrollIntoView` desplaza también a
       los ancestros: en la carga inicial dejaba la aplicación arrancada 250 px
       hacia abajo, con el encabezado de la vista fuera de pantalla.
    */
    const list = listRef.current
    if (list && list.scrollWidth > list.clientWidth) {
      const left = btn.offsetLeft
      const right = left + btn.offsetWidth
      if (left < list.scrollLeft) {
        list.scrollLeft = Math.max(0, left - 8)
      } else if (right > list.scrollLeft + list.clientWidth) {
        list.scrollLeft = right - list.clientWidth + 8
      }
    }
  }, [current, items])

  useEffect(() => {
    const list = listRef.current
    if (!list || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const btn = tabRefs.current.get(current)
      if (!btn) return
      xSpring.current!.set(btn.offsetLeft)
      wSpring.current!.set(btn.offsetWidth)
    })
    ro.observe(list)
    return () => ro.disconnect()
  }, [current])

  const move = (delta: number) => {
    const index = items.findIndex((i) => i.id === current)
    const next = items[(index + delta + items.length) % items.length]
    if (next) {
      onNavigate(next.id)
      tabRefs.current.get(next.id)?.focus()
    }
  }

  return (
    <header className={`chrome${scrolled ? ' is-scrolled' : ''}`}>
      <div className="chrome__inner">
        {/* Botón, no enlace: un `href` a un fragmento que no es ruta ya no
            navega (el router sólo interpreta `#/…`) y un `href="#/panorama"`
            perdería el ámbito seleccionado. */}
        <button
          type="button"
          className="chrome__brand"
          onClick={() => onNavigate(items[0]!.id)}
          aria-label="Canasta Alimentaria, Monitor BAMX. Ir al panorama"
        >
          <BrandMark />
          <span className="chrome__brand-text">
            <span className="chrome__brand-title">Canasta Alimentaria</span>
            <span className="chrome__brand-sub t-caption ink-3">Monitor BAMX</span>
          </span>
        </button>

        <div className="chrome__tabs-wrap">
          <div className="chrome__tabs" role="tablist" aria-label="Secciones" ref={listRef}>
            <span className="chrome__indicator" ref={indicatorRef} aria-hidden="true" />
            {items.map((item) => {
              const selected = item.id === current
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  /* Solo la pestana activa apunta a un panel: renderizamos un
                     panel a la vez, asi que los demas ids no existen y un
                     aria-controls roto es peor que no tenerlo. */
                  {...(selected ? { 'aria-controls': `panel-${item.id}` } : {})}
                  id={`tab-${item.id}`}
                  tabIndex={selected ? 0 : -1}
                  className={`chrome__tab${selected ? ' is-selected' : ''}`}
                  ref={(node) => {
                    if (node) tabRefs.current.set(item.id, node)
                    else tabRefs.current.delete(item.id)
                  }}
                  onPointerDown={(event) => {
                    if (event.pointerType === 'mouse' && event.button !== 0) return
                    if (!selected) onNavigate(item.id)
                  }}
                  onClick={() => {
                    if (!selected) onNavigate(item.id)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowRight') {
                      event.preventDefault()
                      move(1)
                    } else if (event.key === 'ArrowLeft') {
                      event.preventDefault()
                      move(-1)
                    } else if (event.key === 'Home') {
                      event.preventDefault()
                      const first = items[0]
                      if (first) {
                        onNavigate(first.id)
                        tabRefs.current.get(first.id)?.focus()
                      }
                    } else if (event.key === 'End') {
                      event.preventDefault()
                      const last = items[items.length - 1]
                      if (last) {
                        onNavigate(last.id)
                        tabRefs.current.get(last.id)?.focus()
                      }
                    }
                  }}
                >
                  {item.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="chrome__right">
          {status ? (
            onStatusClick ? (
              <button type="button" className="chrome__status t-caption" onClick={onStatusClick}>
                <span
                  className={`chrome__status-dot chrome__status-dot--${statusTone}`}
                  aria-hidden="true"
                />
                {status}
                {statusTone === 'error' ? ' · con incidencias' : ''}
              </button>
            ) : (
              <span className="chrome__status t-caption">
                <span
                  className={`chrome__status-dot chrome__status-dot--${statusTone}`}
                  aria-hidden="true"
                />
                {status}
                {statusTone === 'error' ? ' · con incidencias' : ''}
              </span>
            )
          ) : null}
          <ThemeButton choice={theme} onChange={setTheme} />
        </div>
      </div>
    </header>
  )
}
